import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isArray, isEmpty, map, uniq } from "lodash";
import {
  faAt,
  faFiles,
  faHashtag,
  faImage,
  faShare,
} from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import FileUploadField from "../../shared/form/fields/FileUploadField";
import DirectoryPickerInput from "../../shared/form/fields/DirectoryPickerInput";
import TagSelect from "../../shared/form/fields/TagSelect";
import { useFormContext } from "react-hook-form";
import { Activity, ActivityObject } from "../../../@types/activities.ts";
import SharedActivity from "../activity/object/attachments/SharedActivity.tsx";

type AttachmentType = "files" | "images" | "mentions" | "tags" | "share";

export default function AttachmentsSelector({
  activityToShare,
  activityType,
}: {
  activityToShare?: Activity["shared_from"] | null;
  activityType?: ActivityObject["type"];
}) {
  const { watch } = useFormContext();
  const [selectedType, setSelectedType] = useState<string | null>(
    activityType === "appointment" ? "tags" : null,
  );
  const [attachmentTypes, setAttachmentTypes] = useState(
    activityType && activityType === "appointment"
      ? ["tags"]
      : ["files", "images", "mentions", "tags"],
  );

  const ICON_LOOKUP = {
    files: faFiles,
    images: faImage,
    mentions: faAt,
    tags: faHashtag,
    share: faShare,
  };

  useEffect(() => {
    if (activityToShare) {
      setAttachmentTypes((prev) => uniq([...prev, "share"]));
      setSelectedType("share");
    }
  }, [activityToShare]);

  const fileAttachments = watch("files");

  useEffect(() => {
    if (fileAttachments && fileAttachments.length > 0) {
      setSelectedType("files");
    }
  }, [fileAttachments]);

  function renderAttachmentTypeFields(type: AttachmentType) {
    switch (type) {
      case "files":
        return (
          <FileUploadField
            name="files"
            options={{ multiple: true, selectExisting: true }}
          />
        );
      case "images":
        return (
          <FileUploadField
            name="images"
            options={{ multiple: true, isImage: true, sortable: true }}
          />
        );
      case "mentions":
        return (
          <DirectoryPickerInput
            name="mentions"
            endpoint="/directory/members"
            multi
          />
        );
      case "tags":
        return (
          <TagSelect
            name="tags"
            placeholder={I18n.t(
              "js.activitystreams.composer.tag_selection.placeholder",
            )}
            multi
          />
        );
      case "share":
        return activityToShare && <SharedActivity {...activityToShare} />;
    }
  }

  return (
    <>
      <div className="flex gap-2 items-center">
        {map(attachmentTypes, (type) => {
          const attachments = type === "share" ? activityToShare : watch(type);

          return (
            <button
              key={type}
              type="button"
              className={classNames("btn btn-light btn-sm relative", {
                active: selectedType === type,
              })}
              onClick={(e) => {
                e.preventDefault();
                setSelectedType(type);
              }}
            >
              <FontAwesomeIcon icon={ICON_LOOKUP[type]} />
              {!isEmpty(attachments) &&
                (attachments.length > 0 || !isArray(attachments)) && (
                  <span className="absolute! badge rounded-xl bg-unread -right-1.5 !-top-1.5">
                    {attachments.length || 1}
                  </span>
                )}
            </button>
          );
        })}
      </div>
      {map(attachmentTypes, (type: AttachmentType) => (
        <div
          key={type}
          className={classNames("w-full", {
            hidden: selectedType !== type,
          })}
        >
          {renderAttachmentTypeFields(type)}
        </div>
      ))}
    </>
  );
}
